import { ArrowUpDown, CalendarClock, FileClock, Scale } from "lucide-react";
import RoundedCard from "@cle/components/RoundedCard";
import PendingReportMetric from "@/features/cle-compliance/components/pending-reports/PendingReportMetric";
import { formatLessons } from "@cle/utils/lessonLimits";

type ReportPeriodProgressCardProps = {
  deadline: string;
  documentationPeriod?: string;
  missingLessons: number;
  periodRange: string;
  requiredLessons: number;
  /**
   * Lektioner, der bliver til noget: indberettet nu ELLER overført til næste periode.
   * Ikke det rå valg — lektioner, der bortfalder, skal ikke tælles med i overskriften;
   * de står for sig i `droppedLessons`. Fremskridtsbjælken klipper selv ved 100 %.
   *
   * Send IKKE dette tal til ReportSubmitDialog: dér skal "du har valgt X af Y krævede,
   * der mangler Z" gå op, og det gør kun de lektioner, der faktisk indberettes.
   */
  selectedLessons: number;
  /**
   * Lektioner, der bortfalder — både over kategoriloftet og over lektionskravet. Opstår,
   * når overskuddet ligger i en kategori, der ikke kan overføres. Uden dette læste 14/12
   * som "0 lektioner mangler", hvilket er sandt, men intetsigende.
   */
  droppedLessons?: number;
  title?: string;
  /**
   * Retningen skal være eksplicit. Kortet deles af to flows, der mener det modsatte:
   * efteruddannelsesperioden viser lektioner overført IND fra forrige periode, mens
   * påbud/pålæg viser, hvad der overføres UD til næste. Samme etiket til begge var
   * direkte misvisende.
   */
  transferredInLessons?: number;
  transferredOutLessons?: number;
};

const ReportPeriodProgressCard = ({
  deadline,
  documentationPeriod,
  missingLessons,
  periodRange,
  requiredLessons,
  selectedLessons,
  droppedLessons = 0,
  title = "Efteruddannelsesperiode",
  transferredInLessons,
  transferredOutLessons,
}: ReportPeriodProgressCardProps) => {
  // requiredLessons kan være 0 (paakraevedeLektioner er en float uden nedre grænse):
  // 0/0 giver NaN og "width: NaN%", og selected>0 giver Infinity.
  const progress =
    requiredLessons > 0
      ? Math.min((selectedLessons / requiredLessons) * 100, 100)
      : 0;
  // "Mangler" og "bortfalder" er to forskellige oplysninger. Vis begge, naar begge
  // gaelder — ellers skjuler et bortfald, at kravet slet ikke er naaet.
  const statusParts = [
    missingLessons > 0
      ? `${formatLessons(missingLessons)} lektioner mangler`
      : null,
    droppedLessons > 0
      ? `${formatLessons(droppedLessons)} lektioner bortfalder`
      : null,
  ].filter((part): part is string => part !== null);
  if (statusParts.length === 0) {
    statusParts.push("0 lektioner mangler");
  }

  const transferred =
    transferredInLessons !== undefined
      ? {
          label: "Overført til denne periode",
          value: formatLessons(transferredInLessons),
        }
      : transferredOutLessons !== undefined
        ? {
            label: "Overføres til næste periode",
            value: formatLessons(transferredOutLessons),
          }
        : null;

  return (
    <RoundedCard className="cle-report-detail__progress-card  ">
      <div className="d-flex justify-content-between gap-3">
        <div>
          <h1 className="cle-afventende-indberetninger-card__subtitle">
            {title}
          </h1>
          <p className="cle-report-detail__period-title small mb-0">
            {periodRange}
          </p>
        </div>
        <div className="text-end flex-shrink-0">
          <p className="cle-report-detail__lesson-total mb-0">
            {formatLessons(selectedLessons)} / {formatLessons(requiredLessons)}
          </p>
          <p className="small mb-0">lektioner</p>
        </div>
      </div>
      <hr
        className="cle-afventende-indberetninger-card__divider my-3"
        aria-hidden="true"
      />

      <div className="d-flex gap-6 align-items-center">
        <div className="">
          <PendingReportMetric
            icon={<CalendarClock size={17} strokeWidth={2} />}
            iconVariant="warning"
            label="Frist for indberetning"
            value={deadline}
          />
        </div>
        {documentationPeriod ? (
          <div className="">
            <PendingReportMetric
              icon={<FileClock size={17} strokeWidth={2} />}
              iconVariant="warning"
              label="Dokumentationsperiode"
              value={documentationPeriod}
            />
          </div>
        ) : null}
        <div className="">
          <PendingReportMetric
            icon={<Scale size={17} strokeWidth={2} />}
            iconVariant="warning"
            label="Lektionskrav"
            value={formatLessons(requiredLessons)}
          />
        </div>

        {transferred ? (
          <div className="">
            <PendingReportMetric
              icon={<ArrowUpDown size={17} strokeWidth={2} />}
              iconVariant="info"
              label={transferred.label}
              value={transferred.value}
            />
          </div>
        ) : null}
      </div>
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted small">Fremskridt</span>
          <span
            className="cle-report-detail__missing-lessons"
            style={{ color: missingLessons > 0 ? "#991b1b" : "#64748b" }}
          >
            {statusParts.join(" · ")}
          </span>
        </div>
        <div
          className="progress cle-report-detail__progress"
          role="progressbar"
          aria-valuenow={selectedLessons}
          aria-valuemin={0}
          aria-valuemax={requiredLessons > 0 ? requiredLessons : undefined}
          aria-label="Fremskridt for indberetning"
        >
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </RoundedCard>
  );
};

export default ReportPeriodProgressCard;
